import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { GetIntellexusDate } from "../../../../components/forms/common/intellexusDate/IntellexusDateField";
import useVersionTeamQuery from "../../../../hooks/query/connections/useVersionTeamQuery";
import { languageBasedTypes } from "../../../../hooks/logic/connections/versionTeamConnectionLogic";
import { SystemLanguages } from "../../../../shared/enums/shared-enums";
import {
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS,
  SystemTeamContributionTypes,
  VersionTeamConnection,
} from "../../../../types/versionTeam";
import { Version } from "../../../../types/version";

interface VersionTeamsViewSectionProps {
  anchor: Version;
  systemLanguageType: VersionTeamConnection["system_language_type"];
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const formatTeamContributionType = (
  type: SystemTeamContributionTypes | null
): string => (type && SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[type]) || "N/A";

const formatRevision = (revision: VersionTeamConnection["revision"]): string =>
  Array.isArray(revision) && revision.length > 0
    ? revision
        .map((item) => item?.revision)
        .filter(Boolean)
        .join("\n") || "N/A"
    : "N/A";

const getVersionTeamFields = (edge: VersionTeamConnection): ViewAccordionField[] => {
  const fields: ViewAccordionField[] = [
    {
      label: "Team Contribution Type",
      value: (
        <Typography variant="body1">
          {formatTeamContributionType(
            edge?.system_team_contribution_type ?? null
          )}
        </Typography>
      ),
    },
    {
      label: "Date",
      value: (
        <Typography variant="body1">
          {edge?.date ? GetIntellexusDate(edge.date) : "N/A"}
        </Typography>
      ),
    },
  ];

  if (edge?.system_language_type === SystemLanguages.tibetan) {
    fields.push(
      {
        label: "Name",
        value: (
          <Typography variant="body1">{displayText(edge?.name)}</Typography>
        ),
      },
      {
        label: "Place",
        value: edge?.place ? (
          <EntityReferenceComponent entity={edge.place} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        ),
      },
      {
        label: "Place Identification Status",
        value: (
          <Typography variant="body1">
            {displayText(edge?.place_identification_status)}
          </Typography>
        ),
      },
      {
        label: "Revisions",
        value: (
          <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {formatRevision(edge?.revision)}
          </Typography>
        ),
      }
    );
  }

  if (edge?.system_language_type === SystemLanguages.chinese) {
    fields.push(
      {
        label: "Title",
        value: (
          <Typography variant="body1">{displayText(edge?.title)}</Typography>
        ),
      },
      {
        label: "Taisho Catalog No",
        value: (
          <Typography variant="body1">
            {displayText(edge?.taisho_catalog_no)}
          </Typography>
        ),
      },
      {
        label: "Source Language",
        value: (
          <Typography variant="body1">
            {displayText(edge?.source_language)}
          </Typography>
        ),
      }
    );
  }

  return fields;
};

const VersionTeamsViewSection: React.FC<VersionTeamsViewSectionProps> = ({
  anchor,
  systemLanguageType,
}) => {
  const filteredTypes = useMemo(
    () => languageBasedTypes(systemLanguageType),
    [systemLanguageType]
  );
  const { useFetchConnectedEdges } = useVersionTeamQuery(
    filteredTypes[0],
    systemLanguageType
  );
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    anchor?._id,
    "outbound",
    {}
  );

  const items = useMemo<VersionTeamConnection[]>(
    () =>
      (data?.data ?? []).filter((edge) =>
        filteredTypes.includes(edge.system_team_contribution_type)
      ),
    [data, filteredTypes]
  );

  return (
    <ConnectionAccordionSection<VersionTeamConnection>
      title="Version Teams"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(edge, index) => edge?._key ?? `version-team-${index}`}
      getItemTitle={(edge) =>
        edge?.toDoc ? (
          <EntityReferenceComponent entity={edge.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(edge) =>
        formatTeamContributionType(edge?.system_team_contribution_type ?? null)
      }
      getItemFields={(edge) => getVersionTeamFields(edge)}
      emptyText="No version teams"
    />
  );
};

export default VersionTeamsViewSection;
